import {
  PAGE_SIZES,
  DESIGN_SCHEMA_VERSION,
  type HeaderComponent,
  type SavedDesign,
} from '../schemas/design.js'
import { escapeHtml } from './design-tokens.js'
import { showOrInfer } from './field-resolution.js'

/**
 * The legacy `preferences.customize` document shape. This is read-only
 * legacy data: nothing renders it directly anymore — it exists so designs
 * can be synthesized from it (below) for agencies that predate the WYSIWYG
 * editor, and as the seed the client's agency-onboarding flow still writes.
 */
export interface HeaderFooterSection {
  shape?: 'columns-1-image' | 'columns-1-text' | 'columns-2' | 'columns-3'
  showAgencyLogo?: boolean
  showAgencyName?: boolean
  agencyName?: string | null
  agencyDetails?: {
    showAddressLine1?: boolean
    addressLine1?: string | null
    showAddressLine2?: boolean
    addressLine2?: string | null
    showAddressLine3?: boolean
    addressLine3?: string | null
    showPhoneNumber?: boolean
    phoneNumber?: string | null
    showUrl?: boolean
    url?: string | null
    showEmailAddress?: boolean
    emailAddress?: string | null
    showLinkedIn?: boolean
    linkedIn?: string | null
  }
  recruiterDetails?: {
    showName?: boolean
    name?: string | null
    showPhoneNumber?: boolean
    phoneNumber?: string | null
    showEmailAddress?: boolean
    emailAddress?: string | null
    showLinkedIn?: boolean
    linkedIn?: string | null
  }
}

export interface CustomizeData {
  textColor?: string | null
  borderColor?: string | null
  fontFamily?: string | null
  includeHeader?: boolean
  header?: HeaderFooterSection
  includeFooter?: boolean
  footer?: HeaderFooterSection
  includeWatermark?: boolean
  watermark?: {
    text?: string
    color?: string
    transparency?: number
  }
}

/**
 * Extracts the agency contact details from a legacy `customize` document —
 * the values onboarding captures and the profile page edits — keyed the way
 * `{{agency.*}}` design tokens expect them (see design-tokens.ts). Header
 * values win; footer values fill per-field gaps.
 *
 * Show flags are honored via showOrInfer: the preferences endpoint is a
 * deep-merge PATCH, so "cleared" fields keep their last value in storage
 * and are marked hidden via their show flag instead — a hidden value must
 * not leak back through a token.
 */
export function customizeAgencyContact(
  customize: CustomizeData | null | undefined
): { phone?: string; url?: string; email?: string; linkedIn?: string } {
  type Details = NonNullable<HeaderFooterSection['agencyDetails']>
  const sections = [
    customize?.header?.agencyDetails,
    customize?.footer?.agencyDetails,
  ]
  const pick = (
    select: (
      d: Details
    ) => [boolean | null | undefined, string | null | undefined]
  ): string | undefined => {
    for (const details of sections) {
      if (!details) continue
      const [show, value] = select(details)
      if (showOrInfer(show, value) && value?.trim()) return value
    }
    return undefined
  }
  return {
    phone: pick(d => [d.showPhoneNumber, d.phoneNumber]),
    url: pick(d => [d.showUrl, d.url]),
    email: pick(d => [d.showEmailAddress, d.emailAddress]),
    linkedIn: pick(d => [d.showLinkedIn, d.linkedIn]),
  }
}

/**
 * Synthesizes a SavedDesign from an agency's legacy `customize` preferences,
 * so existing branding keeps rendering (via `resolveEffectiveDesign`) until
 * the agency saves a design of their own in the new editor. Fidelity target
 * is "visually equivalent, no lost information" — not pixel-identical to the
 * old CSS template.
 *
 * Recruiter fields: an explicit per-agency override in the old data is
 * baked as static text (preserving that override exactly); an absent
 * override becomes a `{{recruiter.*}}` token so it keeps resolving
 * dynamically per session, matching the old runtime fallback to
 * `User.agencyProfiles`. `agencyName` follows the same pattern against
 * `{{agency.name}}`. `agencyDetails` fields (address/phone/url/email/
 * linkedIn) never had a dynamic fallback in the old model, so they're
 * always baked as static text.
 */

const PAGE = PAGE_SIZES.letter
const MARGIN = 17
// Bands grow/shrink to fit their content (computed per section below),
// matching the legacy template's flow layout. The floor is deliberately
// lower than the legacy template's 80px min-height so sparse headers
// actually hug their content.
const MIN_BAND_HEIGHT = 40
const BAND_PADDING = 12
const CONTENT_WIDTH = PAGE.w - MARGIN * 2
const COLUMN_GAP = 12
const TEXT_LINE_HEIGHT = 1.3
const DEFAULT_TEXT_COLOR = '#0F172A'
const DEFAULT_BORDER_COLOR = '#cbd5e1'
const DEFAULT_BAND_BACKGROUND = '#f8fafc'

let idCounter = 0
function nextId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

function recruiterFieldContent(
  overrideValue: string | null | undefined,
  tokenField: 'name' | 'phone' | 'email' | 'linkedIn'
): string {
  return overrideValue?.trim()
    ? escapeHtml(overrideValue.trim())
    : `{{recruiter.${tokenField}}}`
}

function agencyNameContent(overrideValue: string | null | undefined): string {
  return overrideValue?.trim()
    ? escapeHtml(overrideValue.trim())
    : '{{agency.name}}'
}

function agencyDetailLines(
  details: NonNullable<HeaderFooterSection>['agencyDetails']
): string[] {
  const lines: string[] = []
  const push = (
    show: boolean | null | undefined,
    value: string | null | undefined
  ) => {
    if (showOrInfer(show, value) && value?.trim())
      lines.push(escapeHtml(value.trim()))
  }
  push(details?.showAddressLine1, details?.addressLine1)
  push(details?.showAddressLine2, details?.addressLine2)
  push(details?.showAddressLine3, details?.addressLine3)
  push(details?.showPhoneNumber, details?.phoneNumber)
  push(details?.showUrl, details?.url)
  push(details?.showEmailAddress, details?.emailAddress)
  push(details?.showLinkedIn, details?.linkedIn)
  return lines
}

function recruiterDetailLines(
  recruiter: NonNullable<HeaderFooterSection>['recruiterDetails']
): string[] {
  const lines: string[] = []
  if (showOrInfer(recruiter?.showName, recruiter?.name)) {
    lines.push(recruiterFieldContent(recruiter?.name, 'name'))
  }
  if (showOrInfer(recruiter?.showPhoneNumber, recruiter?.phoneNumber)) {
    lines.push(recruiterFieldContent(recruiter?.phoneNumber, 'phone'))
  }
  if (showOrInfer(recruiter?.showEmailAddress, recruiter?.emailAddress)) {
    lines.push(recruiterFieldContent(recruiter?.emailAddress, 'email'))
  }
  if (showOrInfer(recruiter?.showLinkedIn, recruiter?.linkedIn)) {
    lines.push(recruiterFieldContent(recruiter?.linkedIn, 'linkedIn'))
  }
  return lines
}

interface TextBlockSpec {
  contentLines: string[]
  x: number
  y: number
  width: number
  height: number
  fontSize: number
  textAlign?: 'left' | 'center' | 'right'
}

function textBlockFromSpec(
  spec: TextBlockSpec,
  color: string
): HeaderComponent | null {
  const content = spec.contentLines.filter(Boolean).join('<br/>')
  if (!content) return null
  return {
    id: nextId('text'),
    type: 'text',
    x: spec.x,
    y: spec.y,
    width: spec.width,
    height: spec.height,
    rotation: 0,
    content,
    fontSize: spec.fontSize,
    fontFamily: 'Arial, sans-serif',
    textAlign: spec.textAlign ?? 'left',
    lineHeight: TEXT_LINE_HEIGHT,
    color,
  }
}

const NAME_FONT_SIZE = 20
const DETAIL_FONT_SIZE = 12

function textContentHeight(lineCount: number, fontSize: number): number {
  return Math.ceil(lineCount * fontSize * TEXT_LINE_HEIGHT)
}

function buildSectionComponents(
  idPrefix: string,
  section: HeaderFooterSection | undefined,
  /** Positions the band once its content-fitted height is known. */
  placeBandY: (bandHeight: number) => number,
  logoAssetKey: string | null | undefined,
  textColor: string,
  borderColor: string
): { components: HeaderComponent[]; bandHeight: number } {
  const components: HeaderComponent[] = []
  const shape = section?.shape ?? 'columns-3'
  const showLogo =
    showOrInfer(section?.showAgencyLogo, logoAssetKey) && Boolean(logoAssetKey)

  const agencyName = showOrInfer(section?.showAgencyName, true)
    ? agencyNameContent(section?.agencyName)
    : ''
  const agencyLines = agencyDetailLines(section?.agencyDetails)
  const recruiterLines = recruiterDetailLines(section?.recruiterDetails)

  // Content-fitted band height, mirroring the legacy template's flow layout
  // where the banner row grew with its text. The logo box mirrors the
  // legacy image region too: an ~80px-min row the image was contain-fit
  // into (never stretched), spanning the full band for columns-1-image
  // and roughly a grid column otherwise.
  const logoHeight = shape === 'columns-1-image' ? 100 : 80
  const logoWidth =
    shape === 'columns-1-image' ? CONTENT_WIDTH - BAND_PADDING * 2 : 160
  let contentHeight: number
  if (shape === 'columns-3') {
    contentHeight = Math.max(
      agencyName ? textContentHeight(1, NAME_FONT_SIZE) : 0,
      textContentHeight(agencyLines.length, DETAIL_FONT_SIZE),
      textContentHeight(recruiterLines.length, DETAIL_FONT_SIZE),
      showLogo ? logoHeight : 0
    )
  } else if (shape === 'columns-2') {
    contentHeight = Math.max(
      textContentHeight(
        (agencyName ? 1 : 0) + agencyLines.length,
        DETAIL_FONT_SIZE
      ),
      textContentHeight(recruiterLines.length, DETAIL_FONT_SIZE),
      showLogo ? logoHeight : 0
    )
  } else {
    const textHeight = textContentHeight(
      (agencyName ? 1 : 0) + agencyLines.length + recruiterLines.length,
      DETAIL_FONT_SIZE
    )
    contentHeight =
      shape === 'columns-1-image' && showLogo
        ? logoHeight + 8 + textHeight
        : Math.max(textHeight, showLogo ? logoHeight : 0)
  }
  const bandHeight = Math.max(MIN_BAND_HEIGHT, contentHeight + BAND_PADDING * 2)
  const bandY = placeBandY(bandHeight)

  components.push({
    id: nextId(`${idPrefix}-band`),
    type: 'container',
    x: MARGIN,
    y: bandY,
    width: CONTENT_WIDTH,
    height: bandHeight,
    rotation: 0,
    backgroundColor: DEFAULT_BAND_BACKGROUND,
    borderLinked: true,
    borderTop: { width: 1, style: 'solid', color: borderColor },
    borderRight: { width: 1, style: 'solid', color: borderColor },
    borderBottom: { width: 1, style: 'solid', color: borderColor },
    borderLeft: { width: 1, style: 'solid', color: borderColor },
    radiusLinked: true,
    radiusTL: 8,
    radiusTR: 8,
    radiusBR: 8,
    radiusBL: 8,
    padding: 0,
  })

  const innerX = MARGIN + BAND_PADDING
  const innerY = bandY + BAND_PADDING
  const innerHeight = bandHeight - BAND_PADDING * 2
  const fullInnerWidth = CONTENT_WIDTH - BAND_PADDING * 2

  let textStartX = innerX
  let textWidth = fullInnerWidth
  let textStartY = innerY
  let textHeight = innerHeight

  if (showLogo) {
    components.push({
      id: nextId(`${idPrefix}-logo`),
      type: 'image',
      x: innerX,
      y: innerY,
      width: logoWidth,
      height: logoHeight,
      rotation: 0,
      assetKey: logoAssetKey ?? undefined,
      originalWidth: logoWidth,
      originalHeight: logoHeight,
      fit: 'contain',
    })
    if (shape === 'columns-1-image') {
      textStartY = innerY + logoHeight + 8
      textHeight = Math.max(innerHeight - logoHeight - 8, 1)
    } else {
      textStartX = innerX + logoWidth + COLUMN_GAP
      textWidth = fullInnerWidth - logoWidth - COLUMN_GAP
    }
  }

  if (shape === 'columns-3') {
    // Column alignment mirrors the legacy template: agency name left,
    // agency details centered (middle column), recruiter right-aligned.
    const colWidth = (textWidth - COLUMN_GAP * 2) / 3
    const specs: TextBlockSpec[] = [
      {
        contentLines: [agencyName],
        x: textStartX,
        y: textStartY,
        width: colWidth,
        height: textHeight,
        fontSize: NAME_FONT_SIZE,
      },
      {
        contentLines: agencyLines,
        x: textStartX + colWidth + COLUMN_GAP,
        y: textStartY,
        width: colWidth,
        height: textHeight,
        fontSize: DETAIL_FONT_SIZE,
        textAlign: 'center',
      },
      {
        contentLines: recruiterLines,
        x: textStartX + (colWidth + COLUMN_GAP) * 2,
        y: textStartY,
        width: colWidth,
        height: textHeight,
        fontSize: DETAIL_FONT_SIZE,
        textAlign: 'right',
      },
    ]
    for (const spec of specs) {
      const block = textBlockFromSpec(spec, textColor)
      if (block) components.push(block)
    }
  } else if (shape === 'columns-2') {
    const colWidth = (textWidth - COLUMN_GAP) / 2
    const specs: TextBlockSpec[] = [
      {
        contentLines: [agencyName, ...agencyLines],
        x: textStartX,
        y: textStartY,
        width: colWidth,
        height: textHeight,
        fontSize: DETAIL_FONT_SIZE,
      },
      {
        contentLines: recruiterLines,
        x: textStartX + colWidth + COLUMN_GAP,
        y: textStartY,
        width: colWidth,
        height: textHeight,
        fontSize: DETAIL_FONT_SIZE,
      },
    ]
    for (const spec of specs) {
      const block = textBlockFromSpec(spec, textColor)
      if (block) components.push(block)
    }
  } else {
    // columns-1-text / columns-1-image: single combined column
    const spec: TextBlockSpec = {
      contentLines: [agencyName, ...agencyLines, ...recruiterLines],
      x: textStartX,
      y: textStartY,
      width: textWidth,
      height: textHeight,
      fontSize: DETAIL_FONT_SIZE,
    }
    const block = textBlockFromSpec(spec, textColor)
    if (block) components.push(block)
  }

  return { components, bandHeight }
}

function toRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean
  const r = parseInt(full.slice(0, 2), 16) || 0
  const g = parseInt(full.slice(2, 4), 16) || 0
  const b = parseInt(full.slice(4, 6), 16) || 0
  return `rgba(${r},${g},${b},${Number(alpha.toFixed(2))})`
}

export interface ConvertCustomizeOptions {
  headerLogoAssetKey?: string | null
  footerLogoAssetKey?: string | null
}

// `agencyName` is intentionally NOT threaded in as a per-section fallback
// here: doing so would bake today's Agency.name as static text the moment
// there's no explicit customize override, defeating the `{{agency.name}}`
// token — which exists precisely so a later agency rename keeps resolving
// live instead of going stale. Only an *explicit* customize-level override
// is baked as static text; absence of one always yields the token.
export function convertCustomizeToDesign(
  customize: CustomizeData | null | undefined,
  options: ConvertCustomizeOptions = {}
): SavedDesign {
  idCounter = 0
  const textColor = customize?.textColor ?? DEFAULT_TEXT_COLOR
  const borderColor = customize?.borderColor ?? DEFAULT_BORDER_COLOR

  const includeHeader = customize?.includeHeader ?? true
  const includeFooter = customize?.includeFooter ?? false
  const includeWatermark = customize?.includeWatermark ?? false

  const components: HeaderComponent[] = []

  let headerHeight = 0
  if (includeHeader) {
    const header = buildSectionComponents(
      'header',
      customize?.header,
      () => MARGIN,
      options.headerLogoAssetKey,
      textColor,
      borderColor
    )
    components.push(...header.components)
    headerHeight = header.bandHeight
  }

  let footerHeight = 0
  if (includeFooter) {
    const footer = buildSectionComponents(
      'footer',
      customize?.footer,
      bandHeight => PAGE.h - MARGIN - bandHeight,
      options.footerLogoAssetKey,
      textColor,
      borderColor
    )
    components.push(...footer.components)
    footerHeight = footer.bandHeight
  }

  // The resume block sits flush against the bands — any visible whitespace
  // between the header/footer and the resume text is the uploaded PDF's own
  // page margin.
  const resumeY = includeHeader ? MARGIN + headerHeight : MARGIN
  const resumeBottom = includeFooter
    ? PAGE.h - MARGIN - footerHeight
    : PAGE.h - MARGIN
  components.unshift({
    id: nextId('resume'),
    type: 'resume',
    x: MARGIN,
    y: resumeY,
    width: CONTENT_WIDTH,
    height: Math.max(resumeBottom - resumeY, 1),
    rotation: 0,
  })

  if (includeWatermark && customize?.watermark?.text) {
    const transparency = customize.watermark.transparency ?? 20
    const alpha = Math.max(0, Math.min(1, 1 - transparency / 100))
    components.push({
      id: 'watermark-singleton',
      type: 'watermark',
      x: MARGIN,
      y: PAGE.h / 2 - 60,
      width: CONTENT_WIDTH,
      height: 120,
      rotation: -30,
      content: escapeHtml(customize.watermark.text),
      fontSize: 60,
      fontFamily: 'Arial, sans-serif',
      textAlign: 'center',
      lineHeight: 1,
      color: toRgba(customize.watermark.color ?? '#000000', alpha),
    })
  }

  return {
    version: DESIGN_SCHEMA_VERSION,
    pageSize: 'letter',
    pageMode: 'all',
    firstPage: { components },
    subsequentPages: { components },
  }
}
